<template>
  <!-- Row: Chart -->
  <div class="row f-row h-100">
    <div class="col col-12" style="height:100%; width:100%;">
      <div class="h-100 d-flex flex-column" id="sims-div" style="width: 100%">
        <svg id="svg-sims"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart } from "@/_core/charts/D3Chart";
import { D3HoverSimulations } from "@/_core/charts/D3ChartOptimizerHover";
import {
  colorExpo,
  colorHedge,
  currencyFormat,
  dnCandles,
} from "@/_core/_helpers/_parametric_tools";
import { format } from "d3";

export default {
  name: "SimulationsChart",
  props: {
    asset: {
      required: true,
      default: "N.A",
    },
    baseCurrency: {
      required: true,
      default: {
        isCurrency: true,
        foreign: "USD",
        rate: "EUR/USD",
        base: "EUR",
      },
    },
    chartData: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    minMaxXYAxisPerc: { required: true, default: () => [] },
    minMaxXYAxisSim: { required: true, default: () => [] },
    processData: { required: true },
    renderChartSignal: { required: true },
    showAllSims: { required: true, default: "ALL" },
    showHedged: { required: true, type: Boolean, default: true },
    showLoading: { required: true, type: Boolean, default: false },
    shownStrategy: {
      required: true,
      default: function() {
        return { id: -1, name: null };
      },
    },
    showStrategy: { required: true, type: Boolean, default: true },
    showUnhedged: { required: true, type: Boolean, default: true },
  },
  data() {
    return {
      D3Chart: null,
      filteredData: [],
      logo: require("@/assets/images/symbol_blue.svg"),
    };
  },
  created() {
    this.D3Chart = new D3Chart("sims-div", "svg-sims");
    this.D3ChartHover = new D3HoverSimulations();
  },
  mounted() {
    if (this.chartData.length > 0) {
      let minMax = this.minMaxXYAxisPerc;
      this.filteredData = this.chartData.filter(
        (d) => d.rates >= minMax[0] && d.rates <= minMax[1],
      );
      this.createSimChart();
    }
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
  },
  watch: {
    colorTheme() {
      this.callWidth();
    },
    showAllSims() {
      if (this.chartData.length > 0) {
        let minMax = this.minMaxXYAxisPerc;
        this.filteredData = this.chartData.filter(
          (d) => d.rates >= minMax[0] && d.rates <= minMax[1],
        );
        this.createSimChart();
      }
    },
    renderChartSignal() {
      if (this.chartData.length > 0) {
        let minMax = this.minMaxXYAxisPerc;
        this.filteredData = this.chartData.filter(
          (d) => d.rates >= minMax[0] && d.rates <= minMax[1],
        );
        this.createSimChart();
      }
    },
    chartData() {
      if (this.chartData.length > 0) {
        let minMax = this.minMaxXYAxisPerc;
        this.filteredData = this.chartData.filter(
          (d) => d.rates >= minMax[0] && d.rates <= minMax[1],
        );
        this.createSimChart();
      }
    },
    shownStrategy() {
      if (this.chartData.length > 0) {
        this.createSimChart();
      }
    },
  },
  methods: {
    createSimChart() {
      let minMax =
        this.showAllSims === "ALL"
          ? this.minMaxXYAxisSim
          : this.minMaxXYAxisPerc;
      // === set SVG canvas
      this.svgNode = this.D3Chart.setSVG(
        0.04,
        5,
        0.5,
        55,
        0.1,
        35,
        0.5,
        15,
        this.colorTheme,
        this.logo,
        "dn",
      );
      // === set axis
      this.svgNode = this.D3Chart.setAxis(
        this.svgNode,
        null,
        minMax,
        null,
        50,
        8,
        false,
        [],
        false,
        true,
        false,
        null,
        false,
        true,
        true,
        minMax,
        this.showAllSims === "ALL",
      );
      let filtered =
        this.showAllSims === "ALL" ? this.chartData : this.filteredData;
      let showStrat = this.showStrategy;
      let id = this.shownStrategy.id;
      filtered.forEach((d) => {
        d.result2 = showStrat ? d.rates + d[id] : null;
      });
      // add a vertical line at the min value of the xAxis
      this.svgNode
        .append("line")
        .attr("class", "xAxisLine")
        .attr("x1", 0)
        .attr("y1", 0)
        .attr("x2", 0)
        .attr("y2", this.svgNode.height);
      // x axis label
      let txt1 =
        "Simulated " + (this.baseCurrency.isCurrency ? "Rate" : "Price");
      let txt2 = "Hedged " + (this.baseCurrency.isCurrency ? "Rate" : "Price");
      this.svgNode
        .append("g")
        .append("text")
        .attr("y", 0)
        .attr("dy", ".71em")
        .attr("class", "axis-label")
        .attr(
          "transform",
          `translate(${this.svgNode.width - 20}, ${this.svgNode.height -
            this.svgNode.margin.top -
            10})`,
        )
        .style("text-anchor", "end")
        .style("dominant-baseline", "middle")
        .text(txt1);
      // y axis label
      this.svgNode
        .append("g")
        .append("text")
        .attr("y", 6)
        .attr("dy", ".71em")
        .attr("class", "axis-label")
        .attr(
          "transform",
          `translate(${this.svgNode.width - 15}, ${this.svgNode.height -
            this.svgNode.margin.top -
            15}) rotate(-90)`,
        )
        .style("text-anchor", "start")
        .style("dominant-baseline", "ideographic")
        .text(txt2);
      // axis title
      this.svgNode
        .append("g")
        .append("text")
        .attr("y", 0)
        .attr("dy", ".71em")
        .attr("class", "axis-label")
        .attr("transform", `translate(${this.svgNode.width / 2}, ${21})`)
        .style("text-anchor", "middle")
        .style("dominant-baseline", "ideographic")
        .attr("color", "white")
        .style("font-size", "0.9rem")
        .text("Visualization of Simulation Results:");
      this.svgNode
        .append("g")
        .append("text")
        .attr("y", "1.2rem")
        .attr("dy", ".71em")
        .attr("class", "axis-label")
        .attr("transform", `translate(${this.svgNode.width / 2}, ${21})`)
        .style("text-anchor", "middle")
        .style("dominant-baseline", "ideographic")
        .attr("color", "white")
        .style("font-size", "0.8rem")
        .text("1,000 of 5,000 Simulations Analyzed");
      // ref y = x line
      if (this.showUnhedged) {
        this.svgNode
          .append("line")
          .attr("stroke", colorExpo(this.colorTheme))
          .attr("x1", 0)
          .attr("y1", this.svgNode.height)
          .attr("x2", this.svgNode.width)
          .attr("y2", 0);
      }
      // == selected strategy scatter
      if (this.showStrategy) {
        if (this.shownStrategy.id > -1) {
          this.D3Chart.scatter(
            this.svgNode,
            filtered,
            "rates",
            "result2",
            dnCandles(this.colorTheme),
            0.2,
            0.5,
            1,
            2.5,
          );
        }
      }
      // === portfolio scatter
      if (this.showHedged) {
        this.D3Chart.scatter(
          this.svgNode,
          filtered,
          "rates",
          "result",
          colorHedge(this.colorTheme),
          0.5,
          0.7,
          1,
          2.5,
        );
      }

      // === hover
      this.D3ChartHover.hoverFct(
        this.svgNode,
        filtered,
        "SIMS",
        format(currencyFormat),
        this.asset,
        true,
        null,
        this.colorTheme,
        this.baseCurrency,
      );
    },
    callWidth() {
      if (this.chartData.length > 0) {
        this.createSimChart();
      }
    },
  },
};
</script>

<style scoped></style>
